import type { FocusEventHandler, MouseEventHandler, RefObject } from "react";
import {
  Activity,
  AlertCircle,
  Battery,
  Circle,
  Headphones,
  Mic,
  Music,
  Square,
  Volume2,
  VolumeX,
  X,
  Zap,
} from "lucide-react";
import { LULLABIES, type Lullaby } from "@/constants";
import type { ConnectionState } from "@/types";
import { MonitorDialog } from "@/components/monitor/MonitorDialog";
import type { BabyDeviceInfo } from "./types";
import { getBabyDisplayName } from "./utils";
import { audioFraction, monitoringStatus } from "./monitorPresentation";
import styles from "./MonitoringView.module.scss";

type MonitoringViewProps = {
  selectedBaby: BabyDeviceInfo;
  remoteStream: MediaStream;
  hasVideo: boolean;
  hasAudio: boolean;
  audioLevel: number;
  controlsVisible: boolean;
  connectionState: ConnectionState;
  isMuted: boolean;
  isRecording: boolean;
  recordingTime: number;
  isLullabyPopupOpen: boolean;
  isLullabyPlaying: boolean;
  currentLullabyId: string | null;
  currentLullaby: Lullaby | null;
  motionAlertTimestamp: number | null;
  motionAlertAgo: string;
  videoRef: RefObject<HTMLVideoElement | null>;
  audioRef: RefObject<HTMLAudioElement | null>;
  onScreenTap: MouseEventHandler<HTMLElement>;
  onFocusCapture: FocusEventHandler<HTMLElement>;
  onBlurCapture: FocusEventHandler<HTMLElement>;
  onToggleLullabyPopup: () => void;
  onCloseLullabyPopup: () => void;
  onSelectLullaby: (lullaby: Lullaby) => void;
  onStopLullaby: () => void;
  onDismissMotionAlert: () => void;
  onToggleMute: () => void;
  onToggleRecording: () => void;
  onDisconnect: () => void;
  formatRecordingTime: (seconds: number) => string;
};

export function MonitoringView({
  selectedBaby,
  remoteStream,
  hasVideo,
  hasAudio,
  audioLevel,
  controlsVisible,
  connectionState,
  isMuted,
  isRecording,
  recordingTime,
  isLullabyPopupOpen,
  isLullabyPlaying,
  currentLullabyId,
  motionAlertTimestamp,
  motionAlertAgo,
  videoRef,
  audioRef,
  onScreenTap,
  onFocusCapture,
  onBlurCapture,
  onToggleLullabyPopup,
  onCloseLullabyPopup,
  onSelectLullaby,
  onStopLullaby,
  onDismissMotionAlert,
  onToggleMute,
  onToggleRecording,
  onDisconnect,
  formatRecordingTime,
}: MonitoringViewProps) {
  const level = audioFraction(audioLevel);
  const connected = connectionState === "connected";
  const name = getBabyDisplayName(selectedBaby, 0);
  const secondaryHidden = !controlsVisible && !isLullabyPopupOpen;

  return (
    <section
      className={`${styles.page} ${!hasVideo ? styles.audioPage : ""}`}
      aria-label={`Monitoring ${name}`}
      onClick={onScreenTap}
      onFocusCapture={onFocusCapture}
      onBlurCapture={onBlurCapture}
    >
      <div className={styles.media}>
        <audio ref={audioRef} autoPlay playsInline muted={isMuted} />
        <video
          key={remoteStream.id}
          ref={videoRef}
          className={styles.video}
          autoPlay
          playsInline
          muted
          controls={false}
          style={{ display: hasVideo ? "block" : "none" }}
        />
        {!hasVideo && (
          <div className={styles.audioVisual}>
            <div className={styles.radar} aria-hidden="true">
              <div
                className={styles.ring}
                style={{ transform: `scale(${1 + level * 0.3})` }}
              />
              <div className={styles.core}>
                <Headphones size={36} strokeWidth={1.8} />
              </div>
            </div>
            <h1>{name}</h1>
            <p>Audio only monitoring</p>
          </div>
        )}
      </div>

      <div className={styles.top}>
        <div className={styles.statusRow}>
          <span
            className={`${styles.status} ${!connected ? styles.interrupted : ""}`}
            role="status"
          >
            <span className={styles.dot} aria-hidden="true" />
            {monitoringStatus(connectionState, hasVideo)}
          </span>
          {selectedBaby.batteryLevel != null && (
            <span className={styles.battery}>
              {selectedBaby.isCharging ? (
                <Zap size={16} />
              ) : (
                <Battery size={16} />
              )}
              <span>
                {selectedBaby.batteryLevel}%
                {selectedBaby.batteryLevel <= 15 && !selectedBaby.isCharging
                  ? " · Low battery"
                  : ""}
              </span>
            </span>
          )}
        </div>
        <div className={styles.secondary}>
          <div className={styles.secondaryGroup} hidden={secondaryHidden}>
            <button
              type="button"
              className={styles.tool}
              onClick={onToggleLullabyPopup}
              aria-label={
                isLullabyPlaying ? "Change sleep sound" : "Choose a sleep sound"
              }
              aria-haspopup="dialog"
              aria-expanded={isLullabyPopupOpen}
            >
              <Music size={20} />
              <span>{isLullabyPlaying ? "Sound playing" : "Sleep sounds"}</span>
            </button>
          </div>
          <button
            type="button"
            hidden={secondaryHidden && !isRecording}
            className={`${styles.tool} ${isRecording ? styles.recording : ""}`}
            onClick={onToggleRecording}
            aria-label={isRecording ? "Stop recording" : "Start recording"}
            aria-pressed={isRecording}
          >
            {isRecording ? (
              <Square size={16} fill="currentColor" />
            ) : (
              <Circle size={18} />
            )}
            <span>
              {isRecording
                ? `Recording ${formatRecordingTime(recordingTime)}`
                : "Record"}
            </span>
          </button>
        </div>
        {!connected && (
          <p className={styles.connectionHint}>
            Check that the baby device is still online and streaming.
          </p>
        )}
        {motionAlertTimestamp !== null && (
          <div className={styles.motionAlert}>
            <Activity size={20} aria-hidden="true" />
            <div>
              <span role="status" key={motionAlertTimestamp}>
                Motion detected
              </span>
              <small>{motionAlertAgo}</small>
            </div>
            <button
              type="button"
              onClick={onDismissMotionAlert}
              aria-label="Dismiss motion alert"
            >
              <X size={20} />
            </button>
          </div>
        )}
      </div>

      <div className={styles.bottom}>
        {hasAudio && (
          <div className={styles.audioMeter} aria-hidden="true">
            <Mic size={16} />
            <div className={styles.meterTrack}>
              <div style={{ width: `${level * 100}%` }} />
            </div>
          </div>
        )}
        <div className={styles.essential}>
          <button
            type="button"
            className={`${styles.speaker} ${isMuted ? styles.muted : ""}`}
            onClick={onToggleMute}
            aria-label={isMuted ? "Turn speaker on" : "Mute speaker"}
            aria-pressed={isMuted}
          >
            {isMuted ? <VolumeX size={24} /> : <Volume2 size={24} />}
            <span>{isMuted ? "Muted" : "Speaker"}</span>
            {isMuted && <AlertCircle size={16} aria-hidden="true" />}
          </button>
          <button
            type="button"
            className={styles.stop}
            onClick={onDisconnect}
            aria-label="Stop monitoring"
          >
            <Square size={18} fill="currentColor" />
            <span>Stop</span>
          </button>
        </div>
      </div>

      <MonitorDialog
        open={isLullabyPopupOpen}
        title="Lullabies & white noise"
        onClose={onCloseLullabyPopup}
      >
        <div className={styles.lullabyList}>
          {LULLABIES.map((lullaby) => {
            const playing = currentLullabyId === lullaby.id && isLullabyPlaying;
            return (
              <button
                type="button"
                key={lullaby.id}
                className={`${styles.lullaby} ${playing ? styles.playing : ""}`}
                onClick={() => onSelectLullaby(lullaby)}
                aria-pressed={playing}
              >
                <span className={styles.songInfo}>
                  <strong>{lullaby.name}</strong>
                  <span>{lullaby.artist}</span>
                </span>
                <span className={styles.songMeta}>
                  {lullaby.duration}
                  {playing && <strong>Playing</strong>}
                </span>
              </button>
            );
          })}
        </div>
        {isLullabyPlaying && (
          <button
            type="button"
            className={styles.stopLullaby}
            onClick={onStopLullaby}
          >
            <Square size={16} />
            <span>Stop Lullaby</span>
          </button>
        )}
      </MonitorDialog>
    </section>
  );
}
